import { API_URL } from '@/config';
import { formatDateTime } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Bell, Send, Users, Eye, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${API_URL}/api`;

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewNotification, setPreviewNotification] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    target_type: 'all',
    target_filter_value: '',
    related_event_id: ''
  });

  useEffect(() => { 
    loadData(); 
    requestNotificationPermission();
  }, []);

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch (error) {
        console.log('Notification permission error:', error);
      }
    }
  };

  const loadData = async () => {
    try {
      const [notifRes, eventsRes] = await Promise.all([
        axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } }),
        axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } })
      ]);
      setNotifications(notifRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      const payload = {
        ...formData,
        target_filter_value: formData.target_filter_value || null,
        related_event_id: formData.related_event_id === 'none' ? null : (formData.related_event_id || null)
      };
      
      await axios.post(`${API}/notifications`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Notification sent successfully!');
      
      // Show browser notification preview
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(formData.title, {
          body: formData.message,
          icon: '/logo192.png',
          tag: 'lions-club-notification'
        });
      }
      
      setFormData({ 
        title: '', 
        message: '', 
        target_type: 'all', 
        target_filter_value: '', 
        related_event_id: '' 
      });
      loadData();
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const getTargetLabel = (type, value) => {
    switch (type) {
      case 'all': return 'All Members';
      case 'officers': return 'Officers Only';
      case 'membersByCategory': return `Category: ${value || 'All'}`;
      default: return type;
    }
  };

  const getEventTitle = (eventId) => {
    const event = events.find(e => e.event_id === eventId);
    return event?.title || 'N/A';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-12 w-12 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Notifications</h2>
          <p className="text-muted-foreground">Send in-app and push notifications to members</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ backgroundColor: '#F1F5F9' }}>
          <Bell className="w-4 h-4" style={{ color: '#D4AF37' }} />
          <span className="text-sm font-medium">{notifications.length} sent</span>
        </div>
      </div>
      
      {/* Send Notification Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" style={{ color: '#D4AF37' }} />
            Send New Notification
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Title *</Label>
                <Input 
                  value={formData.title} 
                  onChange={(e) => setFormData({...formData, title: e.target.value})} 
                  placeholder="Notification title"
                  required 
                  data-testid="notif-title" 
                />
              </div>
              
              <div className="md:col-span-2">
                <Label>Message *</Label>
                <Textarea 
                  value={formData.message} 
                  onChange={(e) => setFormData({...formData, message: e.target.value})} 
                  placeholder="Write your notification message here..."
                  rows={4}
                  required 
                  data-testid="notif-message" 
                />
              </div>
              
              <div>
                <Label>Target Audience</Label>
                <Select value={formData.target_type} onValueChange={(v) => setFormData({...formData, target_type: v, target_filter_value: ''})}>
                  <SelectTrigger data-testid="notif-target">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        All Members
                      </div>
                    </SelectItem>
                    <SelectItem value="officers">Officers Only</SelectItem>
                    <SelectItem value="membersByCategory">By Professional Category</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.target_type === 'membersByCategory' && (
                <div>
                  <Label>Category Filter</Label>
                  <Input 
                    value={formData.target_filter_value} 
                    onChange={(e) => setFormData({...formData, target_filter_value: e.target.value})} 
                    placeholder="e.g., Business, Medical, Legal"
                    data-testid="notif-category" 
                  />
                </div>
              )}
              
              <div>
                <Label>Related Event (Optional)</Label>
                <Select value={formData.related_event_id} onValueChange={(v) => setFormData({...formData, related_event_id: v})}>
                  <SelectTrigger data-testid="notif-event">
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {events.map(e => (
                      <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-4 pt-4 border-t">
              <Button 
                type="submit" 
                disabled={sending || !formData.title || !formData.message}
                style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
                data-testid="send-notif-btn"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Notification
                  </>
                )}
              </Button>
              
              {('Notification' in window) && (
                <p className="text-sm text-muted-foreground">
                  Browser notifications: {Notification.permission === 'granted' ? '✅ Enabled' : 
                    Notification.permission === 'denied' ? '❌ Blocked' : '⚠️ Not requested'}
                </p>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
      
      {/* Notification History */}
      <Card>
        <CardHeader>
          <CardTitle>Notification History</CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No notifications sent yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Related Event</TableHead>
                    <TableHead>Sent At</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notifications.map((notif) => (
                    <TableRow key={notif.notification_id}>
                      <TableCell className="font-medium max-w-[200px] truncate">{notif.title}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full text-xs" style={{ backgroundColor: '#F1F5F9' }}>
                          {getTargetLabel(notif.target_type, notif.target_filter_value)}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {notif.related_event_id ? getEventTitle(notif.related_event_id) : '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {notif.sent_at ? formatDateTime(notif.sent_at) : 'Not sent'}
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setPreviewNotification(notif)}
                          data-testid={`view-notif-${notif.notification_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Preview Dialog */}
      <Dialog open={!!previewNotification} onOpenChange={() => setPreviewNotification(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Notification Details</DialogTitle>
          </DialogHeader>
          {previewNotification && (
            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Title</Label>
                <p className="font-semibold text-lg">{previewNotification.title}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Message</Label>
                <p className="whitespace-pre-wrap">{previewNotification.message}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Target</Label>
                  <p>{getTargetLabel(previewNotification.target_type, previewNotification.target_filter_value)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Sent At</Label>
                  <p>{previewNotification.sent_at ? formatDateTime(previewNotification.sent_at) : 'N/A'}</p>
                </div>
              </div>
              {previewNotification.related_event_id && (
                <div>
                  <Label className="text-muted-foreground">Related Event</Label>
                  <p>{getEventTitle(previewNotification.related_event_id)}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationsPage;
